'use client';

import { useState, useEffect, type CSSProperties, type MouseEvent as ReactMouseEvent } from 'react';
import { Singularity } from '@/components/marketing/engine/singularity';
import { Logo } from '@/components/marketing/hero/logo';

/* ── Frosted product surface — a blurred backdrop BEHIND the headline (Apple depth) ── */

interface Signal {
  k: string;
  v: string;
  c: string;
}

interface FrostedBackdropProps {
  shown: boolean;
}

export function FrostedBackdrop({ shown }: FrostedBackdropProps) {
  const SIGNALS: Signal[] = [
    { k: 'Assessment', v: '91 / 100', c: 'var(--text)' },
    { k: 'Interview', v: 'Cleared', c: 'var(--green)' },
    { k: 'Identity', v: 'Verified', c: 'var(--green)' },
    { k: 'Audit trail', v: 'Complete', c: 'var(--green)' },
  ];
  return (
    <div style={{
      position: 'absolute', left: '50%', top: 'clamp(120px, 15vh, 230px)',
      transform: 'translateX(-50%) scale(1.55)', transformOrigin: 'top center',
      width: 'min(680px, 90vw)', zIndex: 2, pointerEvents: 'none',
      filter: 'blur(3px)', opacity: shown ? 'var(--frost-opacity, 0.5)' : 0,
      transition: 'opacity 1.6s var(--ease) .3s',
      WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, #000 22%, #000 60%, transparent 96%)',
      maskImage: 'linear-gradient(to bottom, transparent 0%, #000 22%, #000 60%, transparent 96%)',
    }}>
      <div className="glass" style={{ borderRadius: 'var(--r-lg)', padding: '20px 22px 70px', boxShadow: 'var(--shadow)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Logo size={20} />
            <span style={{ fontSize: '13px', fontWeight: 600 }}>Decision record</span>
          </div>
          <span className="mono" style={{ fontSize: '10px', color: 'var(--text-3)' }}>candidate &middot; PS-4471</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
          {SIGNALS.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '11px 14px', borderRadius: 'var(--r-sm)', background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <span style={{ fontSize: '12.5px', color: 'var(--text-2)' }}>{s.k}</span>
              <span className="mono" style={{ fontSize: '12px', fontWeight: 600, color: s.c }}>{s.v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ───────────────── Hero ───────────────── */

export function Hero() {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const t = [200, 450, 700, 950, 1300, 1700].map((ms, i) => setTimeout(() => setPhase(i + 1), ms));
    return () => t.forEach(clearTimeout);
  }, []);
  const fade = (n: number, y = 16): CSSProperties => ({
    opacity: phase >= n ? 1 : 0,
    transform: phase >= n ? 'translateY(0)' : `translateY(${y}px)`,
    transition: 'opacity .9s var(--ease), transform .9s var(--ease)',
  });

  return (
    <section id="top" style={{ position: 'relative', overflow: 'hidden', paddingBottom: '40px',
      minHeight: '100dvh', display: 'flex', flexDirection: 'column',
      background: 'var(--bg-veil)' }}>
      {/* Ambient illumination cast by the singularity — lights the whole scene, tracks the core */}
      <div style={{ position: 'absolute', left: '50%', top: '40%', transform: 'translate(-50%,-50%)',
        width: 'min(1500px, 160vw)', height: 'min(1100px, 120vh)', zIndex: 0, pointerEvents: 'none',
        background: 'radial-gradient(ellipse at center, color-mix(in srgb, var(--indigo) 16%, transparent) 0%, transparent 60%)',
        opacity: 'var(--sing-glow, 0.55)', mixBlendMode: 'screen', transition: 'opacity .35s linear' }}></div>

      {/* Singularity field (deepest layer) — masked so it dissolves into the page, no hard edges.
          The radial mask shapes the field; a second linear bottom-fade (intersected) forces the
          luminous core to fade to transparent BEFORE the hero's overflow:hidden clip line, so the
          vortex no longer ends on a hard horizontal cut — it dissolves into the field below. */}
      <div style={{ position: 'absolute', left: '50%', top: '36%', transform: 'translateX(-50%)',
        width: 'min(1100px, 130vw)', height: 'min(780px, 86vh)', zIndex: 1, pointerEvents: 'none',
        WebkitMaskImage: 'radial-gradient(ellipse 74% 60% at 50% 45%, #000 48%, rgba(0,0,0,0.55) 68%, transparent 86%), linear-gradient(to bottom, #000 0%, #000 52%, transparent 72%)',
        WebkitMaskComposite: 'source-in',
        maskImage: 'radial-gradient(ellipse 74% 60% at 50% 45%, #000 48%, rgba(0,0,0,0.55) 68%, transparent 86%), linear-gradient(to bottom, #000 0%, #000 52%, transparent 72%)',
        maskComposite: 'intersect' }}>
        <Singularity />
      </div>

      {/* Frosted product surface behind the headline */}
      <FrostedBackdrop shown={phase >= 5} />

      {/* Legibility scrim behind copy */}
      <div style={{ position: 'absolute', left: '50%', top: 'clamp(140px, 17vh, 250px)', transform: 'translateX(-50%)',
        width: 'min(760px, 96vw)', height: '420px', zIndex: 3, pointerEvents: 'none',
        background: 'radial-gradient(ellipse at 50% 40%, var(--bg) 0%, color-mix(in srgb, var(--bg) 55%, transparent) 45%, transparent 72%)' }}></div>

      {/* Hero copy (front) */}
      <div style={{ position: 'relative', zIndex: 5, textAlign: 'center', maxWidth: '820px',
        margin: '0 auto', padding: 'max(150px, 18vh) 24px 0' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '5px 14px', marginBottom: '30px',
          borderRadius: 'var(--r-full)', border: '1px solid var(--border-hi)', background: 'var(--surface)', ...fade(1, 10) }}>
          <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--green)', boxShadow: '0 0 8px var(--green-spill)' }}></span>
          <span className="label" style={{ fontSize: '9px', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>VERIFIED HIRING</span>
        </div>

        <h1 style={{ fontFamily: 'var(--sans)', fontWeight: 700, fontSize: 'var(--h1)', letterSpacing: '-0.045em', lineHeight: 0.96, ...fade(2) }}>
          Every hiring decision
          <br />
          <span className="serif" style={{ fontWeight: 400, fontStyle: 'italic', letterSpacing: '-0.005em',
            color: 'var(--headline-accent)' }}>has a reason.</span>
        </h1>

        <p style={{ fontSize: 'var(--body)', color: 'var(--text-2)', lineHeight: 1.65, maxWidth: '500px', margin: '28px auto 0', ...fade(3, 10) }}>
          VHire verifies assessments, interviews, and identity, then resolves
          them into one decision your team can trace and defend.
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', justifyContent: 'center', marginTop: '38px', ...fade(4, 8) }}>
          {/* VHire is invite-only — the primary CTA opens a request, not a
              signup. "Get Started" pointed at /login, which sent new users
              down a wizard that created a workspace nobody had approved. */}
          <a href="/request-access"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '13px 30px',
            borderRadius: 'var(--r-full)', background: 'var(--indigo)', color: '#fff', textDecoration: 'none', fontSize: '14px', fontWeight: 600,
            boxShadow: '0 0 34px rgba(99,102,241,0.35), inset 0 1px 0 rgba(255,255,255,0.15)', transition: 'transform .2s, box-shadow .3s' }}
            onMouseEnter={(e: ReactMouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 0 50px rgba(99,102,241,0.55), inset 0 1px 0 rgba(255,255,255,0.15)'; }}
            onMouseLeave={(e: ReactMouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 0 34px rgba(99,102,241,0.35), inset 0 1px 0 rgba(255,255,255,0.15)'; }}>
            Request access
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </a>
          <a href="#command" onClick={(e: ReactMouseEvent<HTMLAnchorElement>) => { e.preventDefault(); const el = document.querySelector('#command'); if (el) window.scrollTo({ top: (el as HTMLElement).offsetTop - 70, behavior: 'smooth' }); }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '13px 8px', color: 'var(--text-2)', textDecoration: 'none', fontSize: '13.5px', fontWeight: 500, transition: 'color .2s' }}
            onMouseEnter={(e: ReactMouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.color = 'var(--text)'; }}
            onMouseLeave={(e: ReactMouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.color = 'var(--text-2)'; }}>
            See how it works
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 9l6 6 6-6"/></svg>
          </a>
        </div>
      </div>

      {/* Spacer — absorbs only the leftover viewport space (basis 0) so the hero
          locks to exactly one full page and the singularity centers below the copy */}
      <div style={{ flex: '1 1 0', minHeight: '120px' }}></div>

      {/* Caption below the field */}
      <div style={{ position: 'relative', zIndex: 5, textAlign: 'center', ...fade(6, 8) }}>
        <span className="label" style={{ fontSize: '9px', color: 'var(--text-3)' }}>{'↓'} &nbsp; from scattered signals to a decision you can defend</span>
      </div>
    </section>
  );
}
